import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DealDetails, UpdateSupplierNote } from '../models/deal-details.model';
import { HttpClient } from '@angular/common/http';
import { Cancellation, RequestCancellation } from '../models/cancellation.model';
import { Assessment, UpdateAssessment } from '../models/assessment.model';
import { API_BASE_URL } from '../injection-tokens';
import { Approval, ApprovalRequirementsResponse, RequestApproval } from '../models/approval.model';
import { Delivery, UpdateDelivery } from '../models/delivery.model';
import { FileItem } from '../models/file-item.model';
import { DealFile } from '../models/deal-file.model';
import { PostProduct, Product } from '../models/product.model';

@Injectable({
  providedIn: 'root',
})
export class DealService {
  private http = inject(HttpClient);

  private apiBaseUrl = inject(API_BASE_URL);

  getById(id: string): Observable<DealDetails> {
    return this.http.get<DealDetails>(`${this.apiBaseUrl}/v1/deals/${id}`);
  }

  requestCancellation(id: string, request: RequestCancellation): Observable<Cancellation> {
    return this.http.patch<Cancellation>(`${this.apiBaseUrl}/v1/deals/${id}/request-cancellation`, request);
  }

  getRequestApprovalRequirements(id: string): Observable<ApprovalRequirementsResponse> {
    return this.http.get<ApprovalRequirementsResponse>(
      `${this.apiBaseUrl}/v1/deals/${id}/request-approval-requirements`,
    );
  }
  requestApproval(id: string, request: RequestApproval): Observable<Approval> {
    return this.http.patch<Approval>(`${this.apiBaseUrl}/v1/deals/${id}/request-approval`, request);
  }

  updateAssessment(id: string, request: UpdateAssessment): Observable<Assessment> {
    return this.http.put<Assessment>(this.apiBaseUrl + `/v1/deals/${id}/assessment`, request);
  }

  updateAssessmentReport(id: string, fileItem: FileItem): Observable<DealFile> {
    const formData = new FormData();
    formData.append('file', fileItem.file, fileItem.file.name);

    return this.http.post<DealFile>(
      this.apiBaseUrl + `/v1/deals/${id}/assessment/final-offer-report`,
      formData,
    );
  }

  deleteAssessmentReport(id: string): Observable<string> {
    return this.http.delete<string>(this.apiBaseUrl + `/v1/deals/${id}/assessment/final-offer-report`);
  }

  updateDelivery(id: string, request: UpdateDelivery): Observable<Delivery> {
    return this.http.put<Delivery>(this.apiBaseUrl + `/v1/deals/${id}/delivery`, request);
  }

  updateDeliveryInvoice(id: string, fileItem: FileItem): Observable<DealFile> {
    const formData = new FormData();
    formData.append('file', fileItem.file, fileItem.file.name);

    return this.http.post<DealFile>(this.apiBaseUrl + `/v1/deals/${id}/delivery/invoice`, formData);
  }

  deleteDeliveryInvoice(id: string): Observable<string> {
    return this.http.delete<string>(this.apiBaseUrl + `/v1/deals/${id}/delivery/invoice`);
  }

  updateDeliveryReport(id: string, fileItem: FileItem): Observable<DealFile> {
    const formData = new FormData();
    formData.append('file', fileItem.file, fileItem.file.name);

    return this.http.post<DealFile>(this.apiBaseUrl + `/v1/deals/${id}/delivery/report`, formData);
  }

  deleteDeliveryReport(id: string): Observable<string> {
    return this.http.delete<string>(this.apiBaseUrl + `/v1/deals/${id}/delivery/report`);
  }

  updateDeliveryDocument(id: string, fileItems: FileItem[]): Observable<DealFile[]> {
    const formData = new FormData();
    fileItems.forEach((fileItem) => {
      formData.append('files', fileItem.file, fileItem.file.name);
    });

    return this.http.post<DealFile[]>(this.apiBaseUrl + `/v1/deals/${id}/documents`, formData);
  }

  deleteDeliveryDocument(id: string, documentId: string): Observable<string> {
    return this.http.delete<string>(`${this.apiBaseUrl}/v1/deals/${id}/documents/${documentId}`);
  }

  getFileDownloadUrl(id: string, fileId: string): Observable<string> {
    return this.http.get<string>(`${this.apiBaseUrl}/v1/deals/${id}/files/${fileId}/url`);
  }

  deleteProduct(id: string, lineItemId: string): Observable<string> {
    return this.http.delete<string>(`${this.apiBaseUrl}/v1/deals/${id}/products/${lineItemId}`);
  }

  deductProduct(id: string, lineItemId: string, capacityUnit: string): Observable<Product> {
    return this.http.post<Product>(
      `${this.apiBaseUrl}/v1/deals/${id}/products/${lineItemId}/deduct?capacityUnit=${encodeURIComponent(capacityUnit)}`,
      null,
    );
  }

  addProduct(dealId: string, product: PostProduct): Observable<Product> {
    return this.http.post<Product>(`${this.apiBaseUrl}/v1/deals/${dealId}/products`, product);
  }

  updateProduct(dealId: string, product: PostProduct): Observable<Product> {
    return this.http.put<Product>(`${this.apiBaseUrl}/v1/deals/${dealId}/products/${product.id}`, product);
  }

  updateSupplierNote(dealId: string, request: UpdateSupplierNote): Observable<string> {
    return this.http.patch<string>(`${this.apiBaseUrl}/v1/deals/${dealId}/update-supplier-note`, request);
  }
}
